import React, { useState, useEffect } from 'react';
import api from '../axiosConfig';

const ScenarioBuilder = ({ baseData, days, onScenarioChange }) => {
  // Slider state
  const [priceIncrease, setPriceIncrease] = useState(0);
  const [utilizationImprovement, setUtilizationImprovement] = useState(0);
  const [retentionImprovement, setRetentionImprovement] = useState(0);
  const [avgTicketIncrease, setAvgTicketIncrease] = useState(0);

  // Scenario result state
  const [scenarioResult, setScenarioResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState(null);

  // Calculate scenario whenever sliders change
  useEffect(() => {
    const calculateScenario = async () => {
      // Don't calculate if all sliders are at zero
      if (priceIncrease === 0 && utilizationImprovement === 0 && 
          retentionImprovement === 0 && avgTicketIncrease === 0) {
        setScenarioResult(null);
        return;
      }

      setIsCalculating(true);
      setError(null);

      try {
        const response = await api.post('/efficiency/scenario', {
          days: days,
          changes: {
            price_increase_pct: priceIncrease,
            utilization_improvement_pct: utilizationImprovement,
            retention_improvement_pct: retentionImprovement,
            avg_ticket_increase_pct: avgTicketIncrease,
          },
        });

        setScenarioResult(response.data.data);

        if (onScenarioChange) {
          onScenarioChange(response.data.data);
        }
      } catch (err) {
        setError(err.message);
        setScenarioResult(null);
      } finally {
        setIsCalculating(false);
      }
    };

    // Debounce the calculation slightly
    const timeoutId = setTimeout(calculateScenario, 300);
    return () => clearTimeout(timeoutId);
  }, [priceIncrease, utilizationImprovement, retentionImprovement, avgTicketIncrease, days, onScenarioChange]);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value) => {
    return `${value.toFixed(1)}%`;
  };

  // Reset all sliders
  const handleReset = () => {
    setPriceIncrease(0);
    setUtilizationImprovement(0);
    setRetentionImprovement(0);
    setAvgTicketIncrease(0);
  };

  // Get risk level class
  const getRiskClass = (level) => {
    switch(level?.toLowerCase()) {
      case 'low': return 'risk-low';
      case 'medium': return 'risk-medium';
      case 'high': return 'risk-high';
      default: return '';
    }
  };

  return (
    <div className="scenario-builder-container">
      {/* Header */}
      <div className="scenario-header">
        <h2>What-If Calculator</h2>
        <p className="scenario-subtitle">
          Model the combined impact of price changes, efficiency improvements, and retention strategies
        </p>
      </div>

      {/* Control Panel */}
      <div className="scenario-controls">
        <div className="controls-header">
          <h3>Your Strategy Levers</h3>
          <button className="reset-button" onClick={handleReset}>
            Reset All
          </button>
        </div>

        {/* Price Increase Slider */}
        <div className="slider-control">
          <div className="slider-header">
            <label htmlFor="price-slider">Price Increase</label>
            <span className="slider-value">{formatPercent(priceIncrease)}</span>
          </div>
          <input
            id="price-slider"
            type="range"
            min="0"
            max="10"
            step="0.5"
            value={priceIncrease}
            onChange={(e) => setPriceIncrease(parseFloat(e.target.value))}
            className="slider"
          />
          <div className="slider-labels">
            <span>0%</span>
            <span>10%</span>
          </div>
          <p className="slider-note">
            Assumes 0.5% client loss per 1% increase (industry average)
          </p>
        </div>

        {/* Utilization Improvement Slider */}
        <div className="slider-control">
          <div className="slider-header">
            <label htmlFor="utilization-slider">Utilisation Improvement</label>
            <span className="slider-value">{formatPercent(utilizationImprovement)}</span>
          </div>
          <input
            id="utilization-slider"
            type="range"
            min="0"
            max="20"
            step="1"
            value={utilizationImprovement}
            onChange={(e) => setUtilizationImprovement(parseFloat(e.target.value))}
            className="slider"
          />
          <div className="slider-labels">
            <span>0%</span>
            <span>20%</span>
          </div>
          <p className="slider-note">
            Fill gaps in schedule, reduce downtime (15% marginal cost)
          </p>
        </div>

        {/* Retention Improvement Slider */}
        <div className="slider-control">
          <div className="slider-header">
            <label htmlFor="retention-slider">Retention Improvement</label>
            <span className="slider-value">{formatPercent(retentionImprovement)}</span>
          </div>
          <input
            id="retention-slider"
            type="range"
            min="0"
            max="10"
            step="0.5"
            value={retentionImprovement}
            onChange={(e) => setRetentionImprovement(parseFloat(e.target.value))}
            className="slider"
          />
          <div className="slider-labels">
            <span>0%</span>
            <span>10%</span>
          </div>
          <p className="slider-note">
            Reduce client churn (saves £25 acquisition cost per client)
          </p>
        </div>

        {/* Average Ticket Increase Slider */}
        <div className="slider-control">
          <div className="slider-header">
            <label htmlFor="ticket-slider">Average Ticket Increase</label>
            <span className="slider-value">{formatPercent(avgTicketIncrease)}</span>
          </div>
          <input
            id="ticket-slider"
            type="range"
            min="0"
            max="15"
            step="0.5"
            value={avgTicketIncrease}
            onChange={(e) => setAvgTicketIncrease(parseFloat(e.target.value))}
            className="slider"
          />
          <div className="slider-labels">
            <span>0%</span>
            <span>15%</span>
          </div>
          <p className="slider-note">
            Upselling, add-ons, retail (10% marginal cost)
          </p>
        </div>
      </div>

      {/* Results Display */}
      {isCalculating && (
        <div className="scenario-calculating">
          <div className="spinner"></div>
          <span>Calculating scenario...</span>
        </div>
      )}

      {error && (
        <div className="scenario-error">
          <p>Error calculating scenario: {error}</p>
        </div>
      )}

      {scenarioResult && !isCalculating && (
        <div className="scenario-results">
          {/* Summary Cards */}
          <div className="results-summary">
            <div className="summary-card net-benefit">
              <span className="summary-label">Net Annual Benefit</span>
              <span className="summary-value">
                {formatCurrency(scenarioResult.net_benefit)}
              </span>
            </div>

            <div className="summary-card risk-level">
              <span className="summary-label">Risk Assessment</span>
              <span className={`summary-value ${getRiskClass(scenarioResult.risk_level)}`}>
                {scenarioResult.risk_level}
              </span>
            </div>

            <div className="summary-card implementation">
              <span className="summary-label">Implementation Effort</span>
              <span className="summary-value">
                {scenarioResult.implementation_effort || 'Moderate'}
              </span>
            </div>
          </div>

          {/* Before & After Comparison */}
          <div className="before-after-section">
            <h3>Impact Analysis</h3>
            
            <div className="before-after-grid">
              {/* Current State */}
              <div className="state-column current-state">
                <h4>Current State</h4>
                <div className="state-metrics">
                  <div className="state-metric">
                    <span className="metric-label">Annual Revenue:</span>
                    <span className="metric-value">
                      {formatCurrency(scenarioResult.before.revenue)}
                    </span>
                  </div>
                  <div className="state-metric">
                    <span className="metric-label">Annual Clients:</span>
                    <span className="metric-value">
                      {scenarioResult.before.clients.toLocaleString()}
                    </span>
                  </div>
                  <div className="state-metric">
                    <span className="metric-label">Average Ticket:</span>
                    <span className="metric-value">
                      {formatCurrency(scenarioResult.before.avg_ticket)}
                    </span>
                  </div>
                  <div className="state-metric">
                    <span className="metric-label">Utilisation Rate:</span>
                    <span className="metric-value">
                      {formatPercent(scenarioResult.before.utilization || 0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="state-arrow">
                <svg width="40" height="40" viewBox="0 0 40 40">
                  <path d="M10 20 L30 20 M30 20 L25 15 M30 20 L25 25" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        fill="none"/>
                </svg>
              </div>

              {/* New State */}
              <div className="state-column new-state">
                <h4>With Changes</h4>
                <div className="state-metrics">
                  <div className="state-metric">
                    <span className="metric-label">Annual Revenue:</span>
                    <span className="metric-value success">
                      {formatCurrency(scenarioResult.after.revenue)}
                    </span>
                  </div>
                  <div className="state-metric">
                    <span className="metric-label">Annual Clients:</span>
                    <span className="metric-value">
                      {scenarioResult.after.clients.toLocaleString()}
                    </span>
                  </div>
                  <div className="state-metric">
                    <span className="metric-label">Average Ticket:</span>
                    <span className="metric-value">
                      {formatCurrency(scenarioResult.after.avg_ticket)}
                    </span>
                  </div>
                  <div className="state-metric">
                    <span className="metric-label">Utilisation Rate:</span>
                    <span className="metric-value">
                      {formatPercent(scenarioResult.after.utilization || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Breakdown by Change Type */}
          <div className="breakdown-section">
            <h3>Contribution Breakdown</h3>
            <div className="breakdown-grid">
              {scenarioResult.breakdown?.map((item, index) => (
                <div key={index} className="breakdown-item">
                  <div className="breakdown-header">
                    <span className="breakdown-label">{item.change_type}</span>
                    <span className="breakdown-value">{formatCurrency(item.net_benefit)}</span>
                  </div>
                  <div className="breakdown-details">
                    <div className="breakdown-detail">
                      <span>Gross Gain:</span>
                      <span>{formatCurrency(item.gross_benefit)}</span>
                    </div>
                    <div className="breakdown-detail">
                      <span>Costs:</span>
                      <span className="cost">-{formatCurrency(item.costs)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Risk Factors */}
          {scenarioResult.risk_factors && scenarioResult.risk_factors.length > 0 && (
            <div className="risk-factors-section">
              <h4>Risk Considerations</h4>
              <ul className="risk-factors-list">
                {scenarioResult.risk_factors.map((factor, index) => (
                  <li key={index}>{factor}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Button */}
          <div className="scenario-actions">
            <button className="primary-button" onClick={() => alert('Save functionality coming soon')}>
              Save This Strategy
            </button>
            <button className="secondary-button" onClick={handleReset}>
              Start Over
            </button>
          </div>
        </div>
      )}

      {/* Placeholder when no scenario */}
      {!scenarioResult && !isCalculating && !error && (
        <div className="scenario-placeholder">
          <p>Adjust the sliders above to model different strategies.</p>
          <p>See the combined impact of multiple changes in real-time.</p>
        </div>
      )}

      {/* Methodology Note */}
      <div className="methodology-note">
        <h4>Calculation Methodology</h4>
        <ul>
          <li><strong>Price Increase:</strong> Assumes 0.5% client loss per 1% price increase (conservative industry estimate)</li>
          <li><strong>Utilisation:</strong> 15% marginal cost for products, consumables, and variable overhead</li>
          <li><strong>Retention:</strong> Saves £25 client acquisition cost per retained client (industry average)</li>
          <li><strong>Avg Ticket:</strong> 10% marginal cost for upsold services and retail</li>
          <li><strong>Combined Effect:</strong> Changes interact - price + utilisation affects total revenue multiplicatively</li>
        </ul>
        <p className="methodology-footer">
          All calculations based on your actual data from the last {days} days, annualised.
        </p>
      </div>
    </div>
  );
};

export default ScenarioBuilder;